/**
 * @module ui-settings
 * @description Global UI style guide (brand, colors, typography, effects)
 */

import { existsSync, readFileSync } from 'node:fs'
import { join } from 'node:path'
import type { ModuleManifest, ModuleContext } from '@gzl10/nexus-sdk'
import { uiStyleGuideEntity } from './ui-style-guide.entity.js'

export { uiStyleGuideEntity } from './ui-style-guide.entity.js'

export const uiSettingsModule: ModuleManifest = {
  name: 'ui-settings',
  label: { en: 'Style Guide', es: 'Guía de Estilos' },
  icon: 'mdi:palette-outline',
  description: {
    en: 'Brand identity and visual style configuration',
    es: 'Configuración de identidad de marca y estilo visual'
  },
  type: 'core',
  category: 'settings',
  dependencies: ['logger'],
  definitions: [uiStyleGuideEntity],
  routePrefix: '/ui-settings',

  /**
   * Seed style guide from data/seeds/ui-style-guide.json if it exists.
   * Idempotent: only inserts if no record exists yet.
   *
   * Workflow:
   * 1. Configure style guide in dev via admin UI
   * 2. Export: GET /api/v1/ui-settings/ui-style-guide → save to data/seeds/ui-style-guide.json
   * 3. Commit the seed file
   * 4. On other environments, seed runs automatically on startup
   */
  seed: async (ctx: ModuleContext) => {
    const db = ctx.db.knex

    // Skip if already configured
    const existing = await db('single_records').where('key', 'ui_style_guide').first()
    if (existing) return

    // Look for seed file in project data/seeds/
    const seedPath = join(process.cwd(), 'data', 'seeds', 'ui-style-guide.json')
    if (!existsSync(seedPath)) return

    try {
      const seedData = JSON.parse(readFileSync(seedPath, 'utf-8'))
      await db('single_records').insert({
        key: 'ui_style_guide',
        value: JSON.stringify(seedData)
      })
      ctx.core.logger.info('Seeded ui_style_guide from data/seeds/ui-style-guide.json')
    } catch {
      ctx.core.logger.warn('Failed to parse data/seeds/ui-style-guide.json, skipping seed')
    }
  }
}
